"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OAUTH_CONSENT_ROUTE = exports.OAUTH_PATHS = exports.oauthConnectionListSchema = exports.oauthConnectionSchema = exports.oauthConsentResultSchema = exports.oauthConsentDecisionSchema = exports.oauthConsentRequestSchema = exports.OAUTH_GRANT_TYPES = exports.OAUTH_CODE_CHALLENGE_METHODS = exports.OAUTH_REFRESH_TTL_SECONDS = exports.OAUTH_ACCESS_TTL_SECONDS = exports.OAUTH_REQUEST_TTL_SECONDS = exports.OAUTH_CODE_TTL_SECONDS = exports.OAUTH_REQUEST_PREFIX = exports.OAUTH_CODE_PREFIX = exports.OAUTH_REFRESH_TOKEN_PREFIX = exports.OAUTH_ACCESS_TOKEN_PREFIX = void 0;
const zod_1 = require("zod");
const token_scopes_1 = require("../permissions/token-scopes");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C52 · the OAuth 2.1 wire. `decisions.md` R-44 · `mcp.md` §4.2            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ```
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE CONSENT SCREEN IS OURS AND MUST BE, SO ITS CONTRACT IS HERE   │
 * │ RATHER THAN IN THE API — THE WEB APP RENDERS IT.                      │
 * └───────────────────────────────────────────────────────────────────────┘
 * ```
 *
 * Everything else in this file is somebody else's software talking to us, and for that half this
 * package buys what it always buys: the server validates with these shapes and the screen infers
 * from them, so a field the API can send that the screen cannot read is a type error rather than a
 * blank panel.
 */
/* ── the secrets on the wire ─────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **Four prefixes, for `PERSONAL_ACCESS_TOKEN_PREFIX`'s two reasons.** `RequestContext.resolve`
 * branches on the access token's prefix **before** anything is verified — a non-JWT handed to the
 * JWKS verifier refuses with `bad_token` and the log then blames a verifier that never saw it. And a
 * prefixed secret is greppable: a string beginning `fcf_oat_` in a log or a config file is
 * unambiguously a live credential, which is the property secret scanners are built around.
 */
exports.OAUTH_ACCESS_TOKEN_PREFIX = 'fcf_oat_';
exports.OAUTH_REFRESH_TOKEN_PREFIX = 'fcf_ort_';
exports.OAUTH_CODE_PREFIX = 'fcf_oac_';
exports.OAUTH_REQUEST_PREFIX = 'fcf_oar_';
/* ── lifetimes ───────────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️⚠️ **ONE MINUTE FOR A CODE, BECAUSE IT TRAVELS IN A URL.** OAuth 2.1 §7.5: an authorization code
 * is a bearer credential in a query string, which means a browser history, a `Referer` header and
 * every proxy log on the way. One minute is longer than any honest exchange takes and shorter than
 * anything a person could act on.
 */
exports.OAUTH_CODE_TTL_SECONDS = 60;
/**
 * ⚠️ **Ten minutes for a pending authorization**, because a person may have to sign in first — and
 * possibly enrol a passkey, or find their phone. Shorter than that turns a slow sign-in into an
 * error with no explanation.
 */
exports.OAUTH_REQUEST_TTL_SECONDS = 600;
/**
 * ⚠️ **An hour for an access token.** *"Authorization servers SHOULD issue short-lived access tokens
 * to reduce the impact of leaked tokens."* A hosted connector refreshes; a leaked token expires.
 */
exports.OAUTH_ACCESS_TTL_SECONDS = 3_600;
/**
 * ⚠️ **Thirty days for a refresh token, rotated on every use.** The ceiling that matters is not this
 * number but the rotation: a replayed refresh token revokes the family on the next exchange, so a
 * stolen one is live until either party next refreshes rather than for thirty days.
 */
exports.OAUTH_REFRESH_TTL_SECONDS = 30 * 24 * 3_600;
/* ── the flow's own vocabulary ───────────────────────────────────────────────────────────────── */
/**
 * ⚠️⚠️ **`S256` AND NOTHING ELSE.** *"MCP clients MUST use the `S256` code challenge method when
 * technically capable"*, and `plain` is not a challenge at all against an attacker who can read the
 * authorization request. Advertising only `S256` is also what a conforming client checks: *"if
 * `code_challenge_methods_supported` is absent, the authorization server does not support PKCE and
 * MCP clients MUST refuse to proceed"*.
 */
exports.OAUTH_CODE_CHALLENGE_METHODS = ['S256'];
/**
 * ⚠️⚠️ **ONE GRANT, AND THE ABSENCES ARE R-44's WHOLE POINT.** No implicit, no `client_credentials`,
 * no password grant. `refresh_token` is here because a hosted connector runs with nobody watching
 * and would otherwise stop working every hour.
 */
exports.OAUTH_GRANT_TYPES = ['authorization_code', 'refresh_token'];
/* ── the consent screen ──────────────────────────────────────────────────────────────────────── */
/**
 * What the screen is given to render. ⚠️ **`redirectHost` is separate from `clientName` because one
 * of them is trustworthy.** *"Authorization servers MUST clearly display the redirect URI hostname
 * during authorization"* — the name is the client's own words and can say anything; the hostname is
 * where the code will actually be sent.
 */
exports.oauthConsentRequestSchema = zod_1.z.object({
    clientId: zod_1.z.string(),
    clientName: zod_1.z.string(),
    clientUri: zod_1.z.string().nullable(),
    logoUri: zod_1.z.string().nullable(),
    /** The host the authorization code will be delivered to. The one fact an attacker cannot forge. */
    redirectHost: zod_1.z.string(),
    /**
     * ⚠️ **True when every redirect URI is a loopback address**, which the specification singles out:
     * *"Client ID Metadata Documents cannot prevent `localhost` URL impersonation by themselves …
     * authorization servers SHOULD display additional warnings for `localhost`-only redirect URIs"*.
     */
    localhostOnly: zod_1.z.boolean(),
    scopes: zod_1.z.array(zod_1.z.enum(token_scopes_1.TOKEN_SCOPES)),
    /** The workspaces this person could grant access to. The screen makes them choose one. */
    workspaces: zod_1.z.array(zod_1.z.object({ id: zod_1.z.string(), name: zod_1.z.string() })),
    /** A live consent for this client, if there is one — so the screen can say *"again"*. */
    alreadyGranted: zod_1.z.boolean(),
});
/**
 * The decision. ⚠️ **The scopes are NOT taken from the client's side of the wire a second time** —
 * they are read from the stored request. What the person may do here is approve or refuse, and
 * choose the workspace; a scope list in this body would be a place to widen one.
 */
exports.oauthConsentDecisionSchema = zod_1.z.object({
    approve: zod_1.z.boolean(),
    /** Required when approving. Must be one of the workspaces the request offered. */
    workspaceId: zod_1.z.string().optional(),
});
/**
 * ⚠️ **The API returns the URL and the SCREEN navigates**, rather than the API answering a 302. A
 * redirect in response to a `fetch` is followed by the browser transparently and the app would never
 * see it; and an approval is an authenticated XHR, not a form post.
 */
exports.oauthConsentResultSchema = zod_1.z.object({ redirectTo: zod_1.z.string() });
/**
 * One application a person has connected, for the account settings screen.
 *
 * ⚠️ **It exists because consent is stored server-side**, which is what makes *"disconnect"* a thing
 * the product can offer at all — and a consent nobody can revoke is a consent that was never really
 * asked for.
 */
exports.oauthConnectionSchema = zod_1.z.object({
    clientId: zod_1.z.string(),
    clientName: zod_1.z.string(),
    clientUri: zod_1.z.string().nullable(),
    workspaceId: zod_1.z.string(),
    workspaceName: zod_1.z.string(),
    scopes: zod_1.z.array(zod_1.z.enum(token_scopes_1.TOKEN_SCOPES)),
    grantedAt: zod_1.z.string(),
    lastUsedAt: zod_1.z.string().nullable(),
});
exports.oauthConnectionListSchema = zod_1.z.object({ data: zod_1.z.array(exports.oauthConnectionSchema) });
/**
 * ⚠️ **The addresses, named once.** `auth-paths.ts`'s reason: a path spelled in the controller and
 * again in the client is a constant that does not help when one of them drifts.
 */
exports.OAUTH_PATHS = {
    /** Where a client sends the browser. Public, and the only endpoint that redirects. */
    authorize: '/oauth/authorize',
    /** The consent screen's own read, authenticated. `:request` is the opaque id from the URL. */
    consent: (request) => `/oauth/authorize/${encodeURIComponent(request)}`,
    token: '/oauth/token',
    revoke: '/oauth/revoke',
    connections: '/account/connections',
    connection: (clientId, workspaceId) => `/account/connections/${encodeURIComponent(clientId)}/${encodeURIComponent(workspaceId)}`,
};
/** The screen's own address in the web app, which `GET /oauth/authorize` redirects to. */
exports.OAUTH_CONSENT_ROUTE = '/oauth/consent';
//# sourceMappingURL=contract.js.map